import React, { useEffect, useMemo, useRef } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Box, Flex } from '@chakra-ui/core'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import DraggableMediaGallery from '../../../../../UI/DraggableMediaGallery'
import MediaGallery from 'components/ui/MediaGallery'
import { FileTile } from 'components/ui/MediaGallery/FileTile'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import { useDropzoneHandlers } from 'hooks/useDropzoneHandlers'
import strings from 'constants/strings'
import HiddenFileInput from 'components/ui/Input/HiddenFileInput'
import { AddMediaButton } from '../AddMediaButton'
import { CategorySelector } from '../CategorySelector'
import useUploadService from 'hooks/useUploadService'
import { useFileValidation } from 'hooks/useFileValidation'
import { ImageEditorModal } from 'components/CommonComponents/ImageEditor/ImageEditorModal'
import { useImageDraftEditor } from 'hooks/useImageDraftEditor'
import { useBuilderWidget } from 'components/TasksV2/TaskCreation/contexts/BuilderWidgetContext'
import useLocaleText from 'components/useLocaleText'
import { MediaUploadActions, mediaUploadLiterals } from '../../../../components/MediaUploadActions'
import { useMediaFileUploads } from '../../../../hooks/useMediaFileUploads'
import { hasOnlyUploadedMedia } from '../../../../model/mediaUploads'
import { ReferenceMediaSection } from '../../../../components/ReferenceMediaSection'

interface ImageMediaGalleryProps {
  subtaskPath: string
  enableEdit?: boolean
}

export const ImageMediaGallery: React.FC<ImageMediaGalleryProps> = ({ subtaskPath, enableEdit = false }) => {
  const { control, watch, getValues, setValue } = useFormContext()
  const builder = useBuilderWidget()
  const uploadText = useLocaleText(mediaUploadLiterals)
  const { setIsMediaUploading } = useFormStepContext()
  const { uploadFile } = useUploadService()
  const { fileSizeValidation } = useFileValidation()

  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const formImages: string[] = watch(`${subtaskPath}.content.images`) || []
  const formVideos: string[] = watch(`${subtaskPath}.content.videos`) || []
  const formFiles: string[] = watch(`${subtaskPath}.content.files`) || []

  const {
    isUploading,
    editorOpen,
    editorStartIndex,
    uploadingUrls: imageUploadingUrls,
    failedUrls: imageFailedUrls,
    hasPendingImages,
    setEditorStartIndex,
    setEditorOpen,
    createDraftBlobs,
    uploadLocalBlobs,
    handleEditorSave,
    handleEditorCancel,
    removeDraftImage,
  } = useImageDraftEditor({
    images: formImages,
    onChange: (imgs) => setValue(`${subtaskPath}.content.images`, imgs),
    uploadFile,
  })

  const videos = useMediaFileUploads({
    getUrls: () => getValues(`${subtaskPath}.content.videos`) || [],
    onChange: (urls) => setValue(`${subtaskPath}.content.videos`, urls),
    uploadFile,
  })
  const files = useMediaFileUploads({
    getUrls: () => getValues(`${subtaskPath}.content.files`) || [],
    onChange: (urls) => setValue(`${subtaskPath}.content.files`, urls),
    uploadFile,
  })
  const busy = isUploading || videos.isUploading || files.isUploading || editorOpen
  const uploadingUrls = new Set([...Array.from(imageUploadingUrls), ...Array.from(videos.uploadingUrls)])
  const failedUrls = new Set([...Array.from(imageFailedUrls), ...Array.from(videos.failedUrls)])

  const allMedia = useMemo(() => [...formImages, ...formVideos], [formImages, formVideos])

  const addImages = (selected: File[]) => {
    const files = selected.filter(fileSizeValidation)

    if (!files.length) return

    const { firstNewIndex, blobUrls } = createDraftBlobs(files)

    if (enableEdit) {
      setEditorStartIndex(firstNewIndex)
      setEditorOpen(true)
      setIsMediaUploading(true)
    } else {
      uploadLocalBlobs(blobUrls)
    }
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    addImages(Array.from(event.target.files || []))
    event.target.value = ''
  }

  const addVideos = (selected: File[]) => {
    const validFiles = selected.filter(fileSizeValidation)
    if (validFiles.length) {
      setIsMediaUploading(true)
      videos.add(validFiles)
    }
  }

  const addFiles = (selected: File[]) => {
    const validFiles = selected.filter(fileSizeValidation)
    if (validFiles.length) {
      setIsMediaUploading(true)
      files.add(validFiles)
    }
  }

  const handleVideoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    addVideos(Array.from(event.target.files || []))
    event.target.value = ''
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(event.target.files || []))
    event.target.value = ''
  }

  const handleDrop = async (selected: File[]) => {
    if (busy) return

    addImages(selected.filter((file) => file.type.startsWith('image/')))
    addVideos(selected.filter((file) => file.type.startsWith('video/')))
    if (builder) addFiles(selected.filter((file) => !/^(image|video)\//.test(file.type)))
  }

  const { onDrop, handlePaste } = useDropzoneHandlers({
    onUploadMedia: handleDrop,
    accept: builder ? `${strings.POST_FILE_ACCEPT_EXTENSIONS},image/*,video/*` : 'image/*,video/*',
  })

  const handleImageClick = (imageIndex: number) => {
    setEditorStartIndex(imageIndex)
    setEditorOpen(true)
  }

  const handleRemoveMedia = (index: number) => {
    if (index < formImages.length) {
      removeDraftImage(formImages[index])
      setValue(
        `${subtaskPath}.content.images`,
        formImages.filter((_: string, i: number) => i !== index)
      )
    } else {
      videos.remove(formVideos[index - formImages.length])
    }
  }

  const handleReorderMedia = (startIndex: number, endIndex: number) => {
    const all = [...formImages, ...formVideos]
    const reordered = Array.from(all)
    const [moved] = reordered.splice(startIndex, 1)

    reordered.splice(endIndex, 0, moved)

    const newImages: string[] = []
    const newVideos: string[] = []

    reordered.forEach((url) => {
      if (formVideos.includes(url)) newVideos.push(url)
      else newImages.push(url)
    })
    setValue(`${subtaskPath}.content.images`, newImages)
    setValue(`${subtaskPath}.content.videos`, newVideos)
  }

  const retryMedia = (index: number) => {
    const url = allMedia[index]
    if (index < formImages.length) void uploadLocalBlobs([url])
    else videos.retry(url)
  }

  useEffect(() => {
    setIsMediaUploading(busy)
  }, [busy, setIsMediaUploading])

  return (
    <>
      <DropzoneWrapper
        onDrop={onDrop}
        onPaste={handlePaste}
        disabled={busy}
        role="group"
        aria-label={uploadText.dropHint}
      >
        <ReferenceMediaSection enabled={builder}>
          {builder ? (
            <MediaUploadActions
              disabled={busy}
              onImage={() => imageInputRef.current?.click()}
              onVideo={() => videoInputRef.current?.click()}
              onFile={() => fileInputRef.current?.click()}
              onCapture={(file) => addImages([file])}
            />
          ) : (
            <Flex alignItems="center" justifyContent="space-between" px="10px" py="5px" style={{ columnGap: '10px' }}>
              <CategorySelector subtaskPath={subtaskPath} />
              <AddMediaButton
                onImageUploadClick={() => imageInputRef.current?.click()}
                onVideoUploadClick={() => videoInputRef.current?.click()}
                isLoading={busy}
              />
            </Flex>
          )}

          {(allMedia.length > 0 || (builder && formFiles.length > 0)) && (
            <DraggableMediaGallery
              layout={builder ? 'grid' : 'row'}
              staticItems={
                builder
                  ? formFiles.map((url) => ({
                      id: url,
                      content: (
                        <FileTile
                          url={url}
                          size={90}
                          uploading={files.uploadingUrls.has(url)}
                          failed={files.failedUrls.has(url)}
                          onRemove={files.remove}
                          onRetry={files.retry}
                        />
                      ),
                    }))
                  : undefined
              }
              isDragDisabled={busy}
              attachedImages={formImages}
              attachedVideos={formVideos}
              uploadingUrls={uploadingUrls}
              failedUrls={failedUrls}
              onRetryMedia={retryMedia}
              isShowRemoveIcon={true}
              onRemoveMedia={handleRemoveMedia}
              onReorder={handleReorderMedia}
              onImageClick={enableEdit ? handleImageClick : undefined}
            />
          )}

          {!builder && (
            <MediaGallery
              fileAppearance="type"
              attachedImages={[]}
              attachedVideos={[]}
              attachedFiles={formFiles}
              uploadingUrls={files.uploadingUrls}
              failedUrls={files.failedUrls}
              onRemoveFile={files.remove}
              onRetryFile={files.retry}
            />
          )}
          {(failedUrls.size > 0 || files.failedUrls.size > 0) && <p role="alert">{uploadText.failed}</p>}
        </ReferenceMediaSection>
      </DropzoneWrapper>

      <HiddenFileInput ref={imageInputRef} onChange={handleImageUpload} accept="image/*" multiple />
      <HiddenFileInput ref={videoInputRef} onChange={handleVideoUpload} accept="video/*" multiple />
      <HiddenFileInput ref={fileInputRef} onChange={handleFileUpload} accept="*/*" multiple />

      <Controller
        name={`${subtaskPath}.content.images`}
        control={control}
        defaultValue={[]}
        rules={{ validate: (urls) => hasOnlyUploadedMedia(urls) || uploadText.failed }}
        as={<Box />}
      />
      <Controller
        name={`${subtaskPath}.content.videos`}
        control={control}
        defaultValue={[]}
        rules={{ validate: (urls) => hasOnlyUploadedMedia(urls) || uploadText.failed }}
        as={<Box />}
      />

      <Controller
        name={`${subtaskPath}.content.files`}
        control={control}
        defaultValue={[]}
        rules={{ validate: (urls) => hasOnlyUploadedMedia(urls) || uploadText.failed }}
        as={<Box />}
      />

      {enableEdit && (
        <ImageEditorModal
          images={formImages}
          startIndex={editorStartIndex}
          isOpen={editorOpen}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
          hasLocalImages={hasPendingImages}
        />
      )}
    </>
  )
}
